import { Link, useLocation, useParams } from "react-router-dom";
import { useSEO } from "@/lib/useSEO";

const COMPANY_EMAIL = "info@obnenterprise.co.uk";
const COMPANY_NAME = "OBN Enterprise Ltd.";
const PRODUCT_NAME = "Hearth & Crate";
const EFFECTIVE_DATE = "26 February 2026";

const PAGES = {
  terms: {
    title: "Terms of Service",
    eyebrow: "Legal",
    body: () => (
      <>
        <p>These Terms govern your use of <b>{PRODUCT_NAME}</b> ("the Service") operated by {COMPANY_NAME} ("we", "us"). By creating an account you agree to be bound by them.</p>

        <h2>1. The Service</h2>
        <p>{PRODUCT_NAME} is a business-management tool for online food businesses covering temperature logs, health &amp; safety audits, event planning, invoices, batch &amp; expiry tracking, training records, and staff records.</p>

        <h2>2. Account &amp; workspace</h2>
        <p>You create a private workspace ("business") at sign-up and become its owner. The owner is responsible for who they invite and for the accuracy of the records held in the workspace. Each workspace is logically isolated; we do not share data across tenants.</p>

        <h2>3. Subscription &amp; free trial</h2>
        <p>The Service is offered on a paid subscription: <b>£29 / month</b> or <b>£290 / year</b>. Every new workspace gets a <b>14-day free trial</b>. After the trial, or if your subscription lapses, the workspace becomes read-only — your data is preserved but you cannot create, edit or delete records until you re-subscribe.</p>
        <p>Subscriptions auto-renew until cancelled. You may cancel at any time from <b>Settings → Billing → Manage in Stripe</b>; access continues to the end of the paid period.</p>

        <h2>4. Acceptable use</h2>
        <p>You agree not to use the Service unlawfully, not to attempt to access other workspaces' data, not to reverse-engineer the platform, and not to use it to store data unrelated to legitimate food-business operations.</p>

        <h2>5. Your data</h2>
        <p>You own your data. We process it on your behalf as the data controller is you — the workspace owner — and we are the data processor. See the <Link className="underline" to="/legal/privacy">Privacy Policy</Link> for details. You can export everything you have stored at any time from <b>Settings → Account → Export my data</b>.</p>

        <h2>6. Deletion</h2>
        <p>You can request deletion of your entire workspace at any time from <b>Settings → Account → Delete workspace</b>. Deletion is queued for 30 days so you can change your mind; after the grace period every record is permanently destroyed.</p>

        <h2>7. Availability &amp; limits</h2>
        <p>We aim to keep the Service running 24/7 but we cannot guarantee uninterrupted access. Scheduled maintenance and unforeseen outages may happen. We are not liable for any indirect or consequential loss arising from the use of the Service.</p>

        <h2>8. Changes to these Terms</h2>
        <p>We may update these Terms. Material changes will be communicated by email to the workspace owner. Continued use after the effective date constitutes acceptance.</p>

        <h2>9. Governing law</h2>
        <p>These Terms are governed by the laws of England &amp; Wales. Disputes are subject to the exclusive jurisdiction of the courts of England &amp; Wales.</p>

        <h2>10. Contact</h2>
        <p>Questions? Email <a className="underline" href={`mailto:${COMPANY_EMAIL}`}>{COMPANY_EMAIL}</a>.</p>
      </>
    ),
  },
  privacy: {
    title: "Privacy Policy",
    eyebrow: "Legal",
    body: () => (
      <>
        <p>This Privacy Policy explains what personal data we collect when you use <b>{PRODUCT_NAME}</b> and how we handle it. We operate under UK GDPR and the Data Protection Act 2018.</p>

        <h2>1. Who we are</h2>
        <p>{COMPANY_NAME}, the operator of {PRODUCT_NAME}. Data controller for account-level data; data processor for workspace records you upload. Contact: <a className="underline" href={`mailto:${COMPANY_EMAIL}`}>{COMPANY_EMAIL}</a>.</p>

        <h2>2. What we collect</h2>
        <ul>
          <li><b>Account data:</b> name, email, password hash (bcrypt), workspace name, role.</li>
          <li><b>Workspace records:</b> the data you choose to store (logs, audits, batches, staff, invoices, etc.).</li>
          <li><b>Billing data:</b> Stripe customer ID, subscription status, current period end. Card numbers are <em>not</em> stored — they live with Stripe.</li>
          <li><b>Technical:</b> minimal HTTP-only auth cookies, IP addresses in server logs (rolling 30 days).</li>
        </ul>

        <h2>3. Why we process it</h2>
        <ul>
          <li><b>To deliver the Service</b> (contract): authentication, storing your records, generating reports.</li>
          <li><b>To take payment</b> (contract): subscription billing via Stripe.</li>
          <li><b>To notify you</b> (legitimate interest): daily digest emails via Brevo if you've enabled them.</li>
          <li><b>Security</b> (legitimate interest): detecting abuse, debugging.</li>
        </ul>

        <h2>4. Sub-processors</h2>
        <ul>
          <li><b>Stripe</b> — payments processor (USA / EU). <a className="underline" href="https://stripe.com/privacy">stripe.com/privacy</a></li>
          <li><b>Brevo (Sendinblue)</b> — transactional email (EU). <a className="underline" href="https://www.brevo.com/legal/privacypolicy/">brevo.com/legal/privacypolicy</a></li>
          <li><b>Google Cloud / hosting</b> — infrastructure for the Service.</li>
        </ul>

        <h2>5. How long we keep your data</h2>
        <p>For as long as your workspace exists. After you delete the workspace there is a 30-day grace period; then every record (records, attachments, users, audit templates, payment history, etc.) is permanently purged.</p>

        <h2>6. Your rights</h2>
        <p>You have the right to access, rectify, port, restrict and erase your personal data. Most of these are self-service in <b>Settings → Account</b> (Export and Delete). For anything else, email us.</p>

        <h2>7. Cookies</h2>
        <p>We only use strictly-necessary cookies (auth session). No analytics, no advertising trackers. See <Link className="underline" to="/legal/cookies">Cookies</Link>.</p>

        <h2>8. International transfers</h2>
        <p>Data may be processed in the EU and/or USA via our sub-processors. We rely on Standard Contractual Clauses where applicable.</p>

        <h2>9. Complaints</h2>
        <p>If you believe we've mishandled your data you can complain to the UK Information Commissioner's Office (ICO) at <a className="underline" href="https://ico.org.uk">ico.org.uk</a>.</p>
      </>
    ),
  },
  cookies: {
    title: "Cookie Policy",
    eyebrow: "Legal",
    body: () => (
      <>
        <p>We use a minimal number of cookies, all of them strictly necessary to operate {PRODUCT_NAME}.</p>

        <h2>Cookies we set</h2>
        <table className="text-sm w-full border border-[#e5e2d9] mt-3">
          <thead className="bg-[#efece6]">
            <tr><th className="text-left p-2">Name</th><th className="text-left p-2">Purpose</th><th className="text-left p-2">Lifetime</th></tr>
          </thead>
          <tbody>
            <tr className="border-t border-[#e5e2d9]"><td className="p-2 font-mono">access_token</td><td className="p-2">Keeps you signed in (HTTP-only, SameSite=Lax)</td><td className="p-2">12 hours</td></tr>
            <tr className="border-t border-[#e5e2d9]"><td className="p-2 font-mono">refresh_token</td><td className="p-2">Lets the app refresh your session silently</td><td className="p-2">7 days</td></tr>
            <tr className="border-t border-[#e5e2d9]"><td className="p-2 font-mono">hearth.cookies-ack</td><td className="p-2">Remembers that you've dismissed the cookie banner</td><td className="p-2">12 months (localStorage)</td></tr>
          </tbody>
        </table>

        <h2>What we don't use</h2>
        <p>No analytics cookies (Google Analytics, Plausible, etc.). No advertising or tracking pixels. No third-party social cookies.</p>

        <h2>Disabling cookies</h2>
        <p>The auth cookies are strictly necessary — if you block them you won't be able to sign in. You can clear them at any time from your browser settings.</p>
      </>
    ),
  },
};

export default function Legal() {
  const { slug } = useParams();
  const { pathname } = useLocation();
  const fromPath = pathname.split("/").pop();
  const candidate = slug || fromPath;
  const key = PAGES[candidate] ? candidate : "terms";
  const page = PAGES[key];
  const Body = page.body;

  const seoBlurb = {
    terms: "The legal terms governing use of Hearth & Crate — the FSA compliance software for UK food businesses.",
    privacy: "How Hearth & Crate handles your data. GDPR-compliant, no advertising trackers, EU-based email processor.",
    cookies: "Hearth & Crate uses only strictly-necessary session cookies. No analytics or advertising trackers.",
  };
  useSEO({
    title: `${page.title} — Hearth & Crate`,
    description: seoBlurb[key],
    canonical: `https://hearthpro.co.uk/legal/${key}`,
  });

  return (
    <div className="min-h-screen bg-[#f7f5f0] text-[#1c1917]" data-testid={`legal-page-${key}`}>
      <header className="border-b border-[#e5e2d9] bg-white">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="font-display text-xl font-medium tracking-tight">
            Hearth<span className="text-[#c0612f]">.</span>
          </Link>
          <nav className="text-xs uppercase tracking-[0.2em] text-[#8a8476] flex items-center gap-5">
            <Link to="/legal/terms" data-testid="legal-nav-terms" className={key === "terms" ? "text-[#1b3b36]" : "hover:text-[#1b3b36]"}>Terms</Link>
            <Link to="/legal/privacy" data-testid="legal-nav-privacy" className={key === "privacy" ? "text-[#1b3b36]" : "hover:text-[#1b3b36]"}>Privacy</Link>
            <Link to="/legal/cookies" data-testid="legal-nav-cookies" className={key === "cookies" ? "text-[#1b3b36]" : "hover:text-[#1b3b36]"}>Cookies</Link>
            <Link to="/login" data-testid="legal-nav-signin" className="text-[#1b3b36] underline underline-offset-2">Sign in</Link>
          </nav>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-12 lg:py-20">
        <div className="text-[11px] uppercase tracking-[0.28em] text-[#8a8476] mb-3">{page.eyebrow}</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-none">{page.title}</h1>
        <div className="text-xs text-[#8a8476] mt-2">Effective {EFFECTIVE_DATE}</div>

        <div className="legal-prose mt-10 text-[#1c1917] leading-relaxed">
          <Body />
        </div>
      </main>

      <footer className="border-t border-[#e5e2d9] mt-12">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 py-6 text-xs text-[#8a8476] flex flex-wrap items-center justify-between gap-3">
          <span>© {new Date().getFullYear()} {COMPANY_NAME}</span>
          <span>Questions? <a className="underline" href={`mailto:${COMPANY_EMAIL}`}>{COMPANY_EMAIL}</a></span>
        </div>
      </footer>
      <style>{`
        .legal-prose { font-size: 14px; line-height: 1.7; }
        .legal-prose p { margin: 0 0 14px; }
        .legal-prose h2 {
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-size: 22px;
          letter-spacing: -0.01em;
          color: #1b3b36;
          margin: 36px 0 10px;
          font-weight: 500;
        }
        .legal-prose ul { margin: 0 0 14px; padding-left: 22px; list-style: disc; }
        .legal-prose li { margin: 4px 0; }
        .legal-prose a { color: #1b3b36; text-decoration: underline; text-underline-offset: 2px; }
        .legal-prose table { width: 100%; }
        .legal-prose th, .legal-prose td { vertical-align: top; }
      `}</style>
    </div>
  );
}
